import { ProgressiveGalleryPhoto } from '@/components/ProgressiveGalleryPhoto'
import { useAuth, useBrowserQuery } from '@/hooks/common'
import NoteInteractions from '@/pages/panels/interactions/Note'
import { Ego, Note, Topic } from '@/types'
import { getFullImageURL, getThumbImageURL } from '@/utils/helper'
import { DeleteOutlineRounded, OpenInNewRounded, PushPinRounded } from '@mui/icons-material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import React, { useMemo, useRef, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import TopicDetailsHeader from './TopicDetailsHeader'
import { RBox } from '@/pages/__styled'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { TopicType } from '@/utils/constants'
import colors from '@/styles/colors'
import { SelectPinCollectionsModal } from './SelectPinCollectionsModal'
import { RModal } from '@/components'
import { useDeleteNote } from '@/hooks/api/dashboard'
import toast from 'react-hot-toast'
import PinOwnerInfoView from './PinOwnerInfoView'

interface FlatEntry {
  noteIndex: number
  imageIndex: number
  image: Ego
  note: Note
}

export interface PostGalleryViewerProps {
  topic: Topic
  notes: Note[]
  initialPostId?: string
  initialImageId?: string
  onChange?: (postIndex: number, imageIndex: number, flatIndex: number) => void
  onBack?: () => void
}

type Direction = 'next' | 'prev'

export function PostGalleryViewer({
  topic,
  notes,
  initialPostId,
  initialImageId,
  onChange,
  onBack,
}: PostGalleryViewerProps) {
  const query = useBrowserQuery()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [showPinSelectionModal, setShowPinSelectionModal] = useState(false)
  const [modalOpened, setModalOpened] = useState(false)
  const deleteNote = useDeleteNote(false)

  const isAlbum = topic.topicType === TopicType.ALBUM.toString()
  const isPinCollection = topic.topicType === TopicType.PIN_COLLECTION.toString()
  const isMyTopic = user?.userID == topic.userID

  const flat = useMemo<FlatEntry[]>(() => {
    const out: FlatEntry[] = []
    notes.forEach((aNote, pIdx) => {
      aNote.egoList.forEach((egoItem, iIdx) => {
        out.push({ noteIndex: pIdx, imageIndex: iIdx, image: egoItem, note: aNote })
      })
    })
    return out
  }, [notes])

  const initialFlatIndex = useMemo(() => {
    if (!initialPostId) return 0
    const idx = flat.findIndex(
      (f) =>
        f.note.noteID === initialPostId &&
        (initialImageId ? f.image.egoID === initialImageId : true),
    )
    return idx === -1 ? 0 : idx
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [flatIndex, setFlatIndex] = useState<number>(initialFlatIndex)
  // Tracks slide direction + a bump counter so the animation replays even
  // when the direction is unchanged from the previous navigation.
  const [direction, setDirection] = useState<Direction>('next')
  const [animTick, setAnimTick] = useState(0)

  const current = flat[flatIndex]
  const activePostIndex = current?.noteIndex ?? 0
  const activeImageIndex = current?.imageIndex ?? 0

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isTyping =
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

      if (isTyping) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        navigateTo((flatIndex - 1 + flat.length) % flat.length, 'prev')
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        navigateTo((flatIndex + 1) % flat.length, 'next')
      } else if (e.key === 'Escape' && onBack) {
        onBack()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [flatIndex, flat.length, onBack])

  useEffect(() => {
    if (current && onChange) {
      onChange(current.noteIndex, current.imageIndex, flatIndex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatIndex])

  const navigateTo = (nextIndex: number, dir: Direction) => {
    setDirection(dir)
    setAnimTick((t) => t + 1)
    setFlatIndex(nextIndex)
  }

  const goPrev = () => navigateTo((flatIndex - 1 + flat.length) % flat.length, 'prev')
  const goNext = () => navigateTo((flatIndex + 1) % flat.length, 'next')

  const jumpTo = (postIndex: number, imageIndex: number) => {
    const idx = flat.findIndex((f) => f.noteIndex === postIndex && f.imageIndex === imageIndex)
    if (idx === -1 || idx === flatIndex) return
    navigateTo(idx, idx > flatIndex ? 'next' : 'prev')
  }

  // Auto-scroll the ribbon so the active group is in view.
  const activeGroupRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    activeGroupRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
    updatePathPath()
  }, [activePostIndex])

  const selectedNote = useMemo(() => {
    if (current) {
      return notes[activePostIndex]
    }
  }, [activePostIndex, notes])

  const updatePathPath = () => {
    if (selectedNote?.noteID) {
      query.set('noteID', selectedNote.noteID)
      navigate(`${pathname}?${query.toString()}`, { replace: true })
    }
  }
  const onClickDeleteNote = () => {
    if (selectedNote) {
      setModalOpened(true)
    }
  }

  const onClickViewOriginal = () => {
    if (selectedNote) {
      const noteTextObject = JSON.parse(selectedNote.noteText)
      const { sourceTopicID, sourceNoteID } = noteTextObject

      window.open(
        `/preview?topicID=${sourceTopicID}&topicType=${TopicType.ALBUM}&noteID=${sourceNoteID}`,
        '_blank',
        'noopener,noreferrer',
      )

      // navigate(
      //   `/preview?topicID=${sourceTopicID}&topicType=${TopicType.ALBUM}&noteID=${sourceNoteID}`,
      // )
    }
  }

  const onDeletePhoto = () => {
    if (!selectedNote) return

    if (activePostIndex == notes.length - 1 && notes.length > 1) {
      goNext()
    }
    toast.promise(
      deleteNote.mutateAsync({
        noteID: selectedNote.noteID,
        topicID: topic.topicID,
      }),
      {
        loading: 'Deleting...',
        success: <Typography variant='p'>Successfully deleted</Typography>,
        error: <Typography variant='p'>Failed to delete</Typography>,
      },
    )
  }

  const onClickPinNote = () => {
    setShowPinSelectionModal(true)
  }

  if (!current) return null

  const animationName = direction === 'next' ? 'pgv-slide-in-right' : 'pgv-slide-in-left'

  return (
    <Box sx={styles.root}>
      {/* Keyframes for the slide transition. Scoped by unique names so they
          don't clash with any other animations in the app. */}
      <Box
        component='style'
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes pgv-slide-in-right {
              from { transform: translateX(6%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
            @keyframes pgv-slide-in-left {
              from { transform: translateX(-6%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          `,
        }}
      />

      {/* Left column: viewer on top, ribbon below — only spans this column's width */}
      <Box sx={styles.leftCol}>
        <Box sx={styles.viewer}>
          <Box sx={styles.blurBgWrap}>
            <Box
              key={`bg-${current.image.egoID}`}
              component='img'
              aria-hidden
              src={current.image.photoFullURL || getFullImageURL(current.image.photoAlbumURL)}
              alt=''
              sx={styles.blurBg}
            />
          </Box>
          <Box sx={styles.blurBgOverlay} />
          {onBack && (
            <IconButton aria-label='Back' onClick={onBack} sx={styles.backBtn}>
              <ArrowBackIosNewIcon fontSize='small' />
            </IconButton>
          )}
          <IconButton
            aria-label='Previous photo'
            onClick={goPrev}
            sx={{ ...styles.navBtn, left: 20 }}
          >
            <ArrowBackIosNewIcon fontSize='medium' />
          </IconButton>

          <Box sx={styles.imageClip}>
            <Box
              key={`${current.image.egoID}-${animTick}`}
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                animation: `${animationName} 280ms ease-out`,
              }}
            >
              <ProgressiveGalleryPhoto
                lowResSrc={getThumbImageURL(current.image.photoAlbumURL)}
                fullResSrc={
                  current.image.photoFullURL || getFullImageURL(current.image.photoAlbumURL)
                }
                mode='full'
              />
            </Box>
          </Box>

          <IconButton aria-label='Next photo' onClick={goNext} sx={{ ...styles.navBtn, right: 20 }}>
            <ArrowForwardIosIcon fontSize='medium' />
          </IconButton>

          {/* <Box sx={styles.counter}>
            <Typography variant='caption' sx={{ color: '#fff' }}>
              {activePostIndex + 1} / {notes.length}
            </Typography>
          </Box> */}

          {/* Floating ribbon: grouped by post, overlaid on the bottom of the viewer */}
          <Box sx={styles.ribbon}>
            <Box sx={styles.ribbonInner}>
              {notes.map((post, pIdx) => {
                const isActiveGroup = pIdx === activePostIndex
                return (
                  <Box
                    key={post.noteID}
                    ref={isActiveGroup ? activeGroupRef : null}
                    sx={{
                      ...styles.group,
                      ...(isActiveGroup ? styles.groupActive : {}),
                    }}
                  >
                    {post.egoList.map((image, iIdx) => {
                      const isActivePhoto = isActiveGroup && iIdx === activeImageIndex
                      return (
                        <Box
                          key={image.egoID}
                          component='button'
                          onClick={() => jumpTo(pIdx, iIdx)}
                          sx={{
                            ...styles.thumbWrap,
                            ...(isActivePhoto ? styles.thumbActive : {}),
                          }}
                          aria-label={`Post ${pIdx + 1}, photo ${iIdx + 1}`}
                        >
                          <Box
                            component='img'
                            src={getThumbImageURL(image.photoAlbumURL)}
                            alt=''
                            sx={{
                              ...styles.thumbImg,
                              ...(isActivePhoto ? styles.thumbImgActive : {}),
                            }}
                          />
                        </Box>
                      )
                    })}
                  </Box>
                )
              })}
            </Box>
          </Box>
          {selectedNote && isPinCollection && (
            <Box sx={styles.pinOwnerView}>
              <PinOwnerInfoView pinNote={selectedNote} onClickViewOriginal={onClickViewOriginal} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Selected post details panel — full height */}
      <Box sx={styles.details}>
        <TopicDetailsHeader
          // showEditForOwner={true}
          topic={topic}
          onClickBack={() => {}}
          onClickComment={() => {}}
          onClickPrivacy={() => {}}
          onDeletedTopic={() => {}}
          // onClickEdit={() => {}}
          onClickViewInteractions={() => {}}
          showBackButton={false}
        />
        {/* <TopicInteractions onClickClose={() => {}} showInteractions={false} /> */}
        {selectedNote && (
          <RBox sx={styles.noteRow}>
            <Typography
              fontSize={'12px'}
              fontWeight={500}
              sx={{ color: '#64748b', letterSpacing: '-0.01em' }}
              whiteSpace={'nowrap'}
            >
              {`Photo ${isPinCollection ? 'pinned' : 'added'} `}
              <CustomTimeAgo date={selectedNote.dateUpdated} />
            </Typography>
            {user && (
              <RBox gap={2}>
                {isPinCollection ? (
                  <>
                    <Tooltip title={'Open Original'}>
                      <Button
                        onClick={onClickViewOriginal}
                        variant='outlined'
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '10px',
                          color: colors.meshdomBlue,
                          borderColor: colors.meshdomBlue,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: colors.meshdomBlue,
                            color: '#fff',
                            borderColor: colors.meshdomBlue,
                          },
                        }}
                      >
                        <OpenInNewRounded fontSize={'small'} />
                      </Button>
                    </Tooltip>

                    {isMyTopic && selectedNote.authorID == user.userID && (
                      <Tooltip title={'Remove Pin'}>
                        <Button
                          onClick={onClickDeleteNote}
                          variant='outlined'
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px',
                            color: colors.meshdomBlue,
                            borderColor: colors.meshdomBlue,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: colors.meshdomBlue,
                              color: '#fff',
                              borderColor: colors.meshdomBlue,
                            },
                          }}
                        >
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <PushPinRounded fontSize={'small'} />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '-10%',
                                width: '120%',
                                height: '2px',
                                backgroundColor: 'currentColor',
                                transform: 'translateY(-50%) rotate(-45deg)',
                              }}
                            />
                          </Box>
                        </Button>
                      </Tooltip>
                    )}
                  </>
                ) : isMyTopic && selectedNote.authorID == user.userID ? (
                  <Tooltip title={'Delete Photo'}>
                    <Button
                      onClick={onClickDeleteNote}
                      variant='outlined'
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                        color: colors.meshdomBlue,
                        borderColor: colors.meshdomBlue,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: colors.meshdomBlue,
                          color: '#fff',
                          borderColor: colors.meshdomBlue,
                        },
                      }}
                    >
                      <DeleteOutlineRounded />
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={onClickPinNote}
                    variant='outlined'
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                      color: colors.meshdomBlue,
                      borderColor: colors.meshdomBlue,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: colors.meshdomBlue,
                        color: '#fff',
                        borderColor: colors.meshdomBlue,
                      },
                    }}
                  >
                    <PushPinRounded />
                    {'Add Pin'}
                  </Button>
                )}
              </RBox>
            )}
          </RBox>
        )}

        <Box sx={styles.interactionsWrap}>
          <NoteInteractions trackKey='topicID' onClickClose={() => {}} showInteractions={false} />
        </Box>
      </Box>

      {showPinSelectionModal && selectedNote && (
        <SelectPinCollectionsModal
          note={selectedNote}
          topic={topic}
          onClose={() => setShowPinSelectionModal(false)}
        />
      )}
      <RModal
        open={modalOpened}
        onClose={() => setModalOpened(false)}
        title={isPinCollection ? 'Remove Pin' : 'Delete Photo'}
        description={`Are you sure you want to ${isPinCollection ? 'remove pin from' : 'delete'} this photo?`}
        submitText='Confirm'
        onSubmit={onDeletePhoto}
      />
    </Box>
  )
}

const ACTIVE_OUTLINE_COLOR = '#FFF'

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    background: '#111',
    fontFamily: 'system-ui, sans-serif',
  },
  leftCol: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    height: '100%',
  },
  viewer: {
    position: 'relative',
    width: '100%',

    height: '100%',
    background: '#333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  imageClip: {
    py: '24px',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
    zIndex: 1,
    position: 'relative', // added — child is absolute now
  },
  blurBgWrap: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden', // clips the scaled child so it can't leak into ancestor scroll calc
  },

  blurBg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'blur(70px) saturate(1.15)',
    transform: 'scale(1.3)',
  },

  blurBgOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
  },

  fullImage: {
    width: '90%',
    height: '90%',
    objectFit: 'contain',
  },

  backBtn: {
    position: 'absolute',
    top: 20,
    left: 20,
    zIndex: 2,
    bgcolor: 'rgba(0,0,0,0.35)',
    color: '#fff',
    width: 40,
    height: 40,
    borderRadius: '8px', // square corners (use '0px' for fully sharp)
    '&:hover': {
      bgcolor: 'rgba(0,0,0,0.5)',
    },
  },

  navBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    bgcolor: 'rgba(0,0,0,0.35)',
    color: '#fff',
    width: 60,
    height: 60,
    '&:hover': {
      bgcolor: 'rgba(0,0,0,0.5)',
    },
    zIndex: 2,
  },

  counter: {
    position: 'absolute',
    bottom: 110,
    left: 20,
    background: 'rgba(0,0,0,0.35)',
    padding: '2px 8px',
    borderRadius: '6px',
  },
  // The details column must set both a bounded height AND minHeight: 0.
  // Without minHeight: 0, this flex column's `interactionsWrap` child
  // (flex: 1) can't shrink below its content height, so the comment list
  // inside NoteInteractions grows the whole panel instead of scrolling.
  details: {
    width: 400,
    height: '100%',
    minHeight: 0,
    background: '#efefef',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  // Wraps NoteInteractions so it gets a bounded, shrinkable box to fill.
  interactionsWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  detailsTitle: { margin: '0 0 8px', fontWeight: 600 },
  detailsDesc: {
    margin: '0 0 12px',
    color: '#444',
    lineHeight: 1.5,
  },
  detailsMeta: { margin: 0, color: '#888' },
  ribbon: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    padding: 2,
    overflowX: 'auto',
    zIndex: 2,
  },
  ribbonInner: {
    display: 'flex',
    gap: 1.5,
    margin: '0 auto', // centers content when it's narrower than the ribbon
    // when content is wider than the ribbon, margin:auto collapses toward 0
    // and the browser scrolls normally from true position 0 — no clipped start
  },
  group: {
    display: 'flex',
    gap: 1,
    padding: 1,
    borderRadius: '6px',
    border: '2px solid transparent',
    flexShrink: 0,
    background: '#FFFFFF4D',
  },
  groupActive: {
    // border: `2px solid ${ACTIVE_OUTLINE_COLOR}`,
    // background: 'rgba(61,43,255,0.08)',
  },
  thumbWrap: {
    padding: 0,
    border: '2px solid transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    background: 'none',
    width: 52,
    height: 52,
    flexShrink: 0,
    overflow: 'hidden',
    transition: 'transform 200ms ease-out',
    // '&:hover': {
    //   transform: 'scale(1.08)',
    // },
  },
  thumbActive: {
    border: `3px solid #FF9D2E`,
    // transform: 'scale(1.08)',
    zIndex: 1,
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    // filter: 'grayscale(0%) brightness(0.85)',
    transition: 'filter 200ms ease-out',
  },
  thumbImgActive: {
    filter: 'grayscale(0%) brightness(1)',
  },
  noteRow: {
    padding: 2,
    justifyContent: 'space-between',
  },

  pinOwnerView: {
    position: 'absolute',
    bottom: 110,
    right: 20,
    background: 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.7) 100%)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    padding: '2px 8px',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    zIndex: 1,
  },
} as const
